import { ApiAppointment } from "../../core/apis";
import { AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Schema } from "rsuite";
import { useHistory } from "react-router";
import { useState, useEffect, useCallback } from "react";
import {
  DateInput,
  NumberInput,
  TextInput,
} from "../../components/textInput.component";
import { ApiSchemaAppointmentById } from "../../models/api/appointment";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { ActionsAppointmentTable } from "../../core/redux/slices/sliceAppointmentTable";
import { UtilityDate } from "../../utils/dateUtils";
import { SpinLoader } from "../../components/spinLoader.component";
import { differenceInDays } from "date-fns/esm";
import { useOverlappedAppointment } from "../../hooks";
import { Link } from "react-router-dom";

type AppointmentFormData = {
  AppointmentStartDate: Date;
  AppointmentEndDate: Date;
  TaskStartTime: Date;
  MaxHours: string;
  caregiverSKey: string;
  Certificates: UploadedFileType[];
};

export const UpdateAppointment = ({
  data,
  isValidating,
  appointmentId,
  showAvailableDays,
}: {
  data: ApiSchemaAppointmentById | undefined;
  isValidating: boolean;
  appointmentId: string;
  showAvailableDays: boolean;
}) => {
  const caregiverName = data?.caregiverName;

  const caregiverSKeyValue = data?.caregiverSKey ? data?.caregiverSKey : "";

  const routeHistory = useHistory();
  const [formError, setFormError] = useState({});
  const [selectedCaregiverSkey, setSelectedCaregiverSkey] = useState<string>();

  const [formData, setFormData] = useState<Partial<AppointmentFormData>>({
    AppointmentStartDate: new Date(),
    AppointmentEndDate: new Date(),
    TaskStartTime: new Date(),
    caregiverSKey: caregiverSKeyValue,
    Certificates: [],
  });
  const { haveIssue, overlappedAppointment } = useOverlappedAppointment({
    patientSKey: data?.patientSKey,
    startDate: formData.AppointmentStartDate,
    endDate: formData.AppointmentEndDate,
    appointmentId: appointmentId,
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    setFormData({
      AppointmentStartDate: new Date(data?.appointmentStartDate || new Date()),
      AppointmentEndDate: new Date(data?.appointmentEndDate || new Date()),
      TaskStartTime: new Date(data?.taskStartTime || new Date()),
      MaxHours: data?.maxHours.toFixed(2) || "0",
      caregiverSKey: caregiverSKeyValue,
    });
    setSelectedCaregiverSkey(data.caregiverSKey!);
  }, [data, caregiverSKeyValue]);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useAppDispatch();

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  const handleSubmit = async () => {
    setIsLoading(true);
    const multipartFormData = generateMultipartFormData(formData);
    multipartFormData.append("SKey", appointmentId);

    multipartFormData.delete("TaskStartTime");
    const startTime = UtilityDate.combineDateTime(
      formData.AppointmentStartDate || new Date(),
      formData.TaskStartTime || new Date()
    );
    multipartFormData.append(
      "TaskStartTime",
      UtilityDate.ToIsoUtcDateTime(startTime)
    );

    const result = await ApiAppointment.Update(multipartFormData);
    if (result.isSuccess) {
      dispatch(ActionsAppointmentTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }

    setIsLoading(false);
  };

  const handleTaskStartTimeChange = (time: Date) => {
    setFormData({ ...formData, TaskStartTime: time });
  };

  useEffect(() => {
    formData?.AppointmentStartDate &&
      formData?.AppointmentEndDate &&
      formData.AppointmentStartDate > formData.AppointmentEndDate &&
      setFormData({
        ...formData,
        AppointmentEndDate: formData.AppointmentStartDate,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.AppointmentStartDate]);

  const handleMaxHoursUpdate = useCallback(() => {
    const totalHours = (data?.totalUnit ?? 0) / 4;
    let totalDays =
      differenceInDays(
        formData.AppointmentEndDate ?? new Date(),
        formData.AppointmentStartDate ?? new Date()
      ) + 1;
    totalDays = 30;
    const totalHoursPerDay = Math.min(16, totalHours / totalDays).toFixed(2);

    totalHoursPerDay &&
      setFormData((prev) => ({ ...prev, MaxHours: totalHoursPerDay }));
  }, [
    data?.totalUnit,
    formData.AppointmentEndDate,
    formData.AppointmentStartDate,
  ]);

  const isBackDated = useCallback(() => {
    if (!formData.AppointmentStartDate) return false;
    const startDate = new Date(formData.AppointmentStartDate);
    if (formData.TaskStartTime) {
      startDate.setHours(
        formData.TaskStartTime.getHours(),
        formData.TaskStartTime.getMinutes()
      );
    }
    const currentDate = new Date();
    if (startDate < currentDate) return true;
    return false;
  }, [formData.AppointmentStartDate, formData.TaskStartTime]);

  useEffect(() => {
    const isEmpty = Object.values({ ...formData }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    //const dateValidated = isBackDated();
    var isDisabled =
      isEmpty ||
      !!errorCount ||
      !!data?.isPatientDeactivated ||
      !!data?.isCaregiverDeactivated 
      // ||
      // !dateValidated;
    setIsDisabled(isDisabled);
  }, [
    data?.isPatientDeactivated,
    data?.isCaregiverDeactivated,
    formData,
    formError,
    showAvailableDays,
    selectedCaregiverSkey,
    data?.sKey,
    isBackDated,
  ]);

  return (
    <Form
      className="row"
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCheck={setFormError}
      formValue={formData}
      model={Schema.Model({
        AppointmentStartDate: Schema.Types.DateType().isRequired(),
        AppointmentEndDate: Schema.Types.DateType().isRequired(),
        TaskStartTime: Schema.Types.DateType().isRequired(),
        MaxHours: Schema.Types.NumberType().max(16).isRequired(),
        Certificates: Schema.Types.ArrayType(),
      })}
      fluid
    >
      <div className="col-md-10 col-12 border-end px-4 pb-3 add-page-ctl">
        {isValidating ? (
          <SpinLoader />
        ) : (
          <>
            {!!data?.isPatientDeactivated && (
              <div className="alert alert-danger text-capitalize" role="alert">
                You can not update an appointment of a deactivated patient.
              </div>
            )}
            {!!data?.isCaregiverDeactivated && (
              <div className="alert alert-danger text-capitalize" role="alert">
                You can not update an appointment of a deactivated caregiver.
              </div>
            )}
            {overlappedAppointment.exist && (
              <div className="alert alert-danger" role="alert">
                Patient have already appointment from{" "}
                {overlappedAppointment.from} to {overlappedAppointment.to}.
                Please{" "}
                <Link
                  to={{
                    pathname: AppRouteUi.Appointment.UpdateById(
                      overlappedAppointment.sKey
                    ),
                    state: { from: "appointment-create" },
                  }}
                  className="ml-2"
                >
                  update
                </Link>{" "}
                that appointment or change the current appointment schedule.
              </div>
            )}
            <div className="row">
              <DateInput
                name="AppointmentStartDate"
                required
                value={formData.AppointmentStartDate}
                onChange={handleMaxHoursUpdate}
              >
                Appointment Start Date
              </DateInput>

              <DateInput
                name="AppointmentEndDate"
                required
                disabledDate={(date) => {
                  return date && formData.AppointmentStartDate
                    ? date < formData.AppointmentStartDate
                    : UtilityDate.isDateBeforeToday(date);
                }}
                onChange={handleMaxHoursUpdate}
                value={formData.AppointmentEndDate}
              >
                Appointment End Date
              </DateInput>
            </div>

            <div className="row">
              <DateInput
                name="TaskStartTime"
                time
                timeChange={handleTaskStartTimeChange}
                required
                value={formData.TaskStartTime}
              >
                Task Start Time
              </DateInput>

              <NumberInput name="MaxHours" min={0} max={16} required disabled>
                Max Hours
              </NumberInput>
            </div>

            {/* {isBackDated() && (
              <div className="row">
                <SelectPickerInput
                  data={requestedCaregivers ?? []}
                  value={selectedCaregiverSkey}
                  searchable={false}
                  onChange={(value: any) => setSelectedCaregiverSkey(value)}
                  name="caregiver"
                  className="w-100"
                  required
                >
                  Select careGiver
                </SelectPickerInput>
              </div>
            )} */}
            <div className="row col-md-6">
              <TextInput
                name="to"
                required
                fullWidth
                value={caregiverName}
                disabled
              >
                Caregiver
              </TextInput>
            </div>

            {/* <FileInput
              listType="text"
              imageSrc={fileUploadLogo}
              name="Certificates"
              required
              multiple
            >
              Upload Certificate
            </FileInput> */}
          </>
        )}
      </div>

      <div className="col-md-2 col-12">
        <Button
          className="m-1 w-100 btn-green"
          type="submit"
          name="Save"
          loading={isLoading}
          disabled={isDisabled || haveIssue}
        />
        <Button
          onClick={() => {
            routeHistory.length > 1
              ? routeHistory.goBack()
              : routeHistory.push(AppRouteUi.Root);
          }}
          type="button"
          className="m-1 w-100 btn-green-subtle"
          disabled={isLoading}
          name="Return without saving"
        />
      </div>
    </Form>
  );
};
